import type { PracticeLocale } from "./practiceLocale";
import type { PracticeBand } from "./practiceBands";
import { isPracticeBand } from "./practiceBands";

export const PRACTICE_CURRICULUM_KEY = "mathboard-practice-curriculum";

export const PRACTICE_CURRICULA = [
  "il",
  "us",
  "ca",
  "uk",
  "au",
  "generic",
] as const;

export type PracticeCurriculumId = (typeof PRACTICE_CURRICULA)[number];

const CURRICULUM_IDS = new Set<string>(PRACTICE_CURRICULA);

export function isPracticeCurriculumId(
  value: unknown,
): value is PracticeCurriculumId {
  return typeof value === "string" && CURRICULUM_IDS.has(value);
}

export function parsePracticeCurriculumId(
  value: unknown,
): PracticeCurriculumId | null {
  return isPracticeCurriculumId(value) ? value : null;
}

/** Hebrew UI defaults to Israel; English defaults to generic bands. */
export function defaultCurriculumForLocale(
  locale: PracticeLocale,
): PracticeCurriculumId {
  return locale === "he" ? "il" : "generic";
}

export function resolvePracticeCurriculumId(
  stored: string | null | undefined,
  locale: PracticeLocale,
): PracticeCurriculumId {
  return parsePracticeCurriculumId(stored) ?? defaultCurriculumForLocale(locale);
}

type BandLabels = Record<PracticeBand, { en: string; he: string }>;

const GENERIC_BAND_LABELS: BandLabels = {
  elementary: { en: "Primary", he: "יסודי" },
  middle: { en: "Lower secondary", he: "חטיבה" },
  high: { en: "Upper secondary", he: "תיכון" },
};

const IL_BAND_LABELS: BandLabels = {
  elementary: { en: "Elementary", he: "יסודי" },
  middle: { en: "Middle school", he: "חטיבה" },
  high: { en: "High school", he: "תיכון" },
};

const CURRICULUM_OPTION_LABEL: Record<
  PracticeCurriculumId,
  { en: string; he: string }
> = {
  il: { en: "Israel", he: "ישראל" },
  us: { en: "United States", he: "ארצות הברית" },
  ca: { en: "Canada", he: "קנדה" },
  uk: { en: "United Kingdom", he: "בריטניה" },
  au: { en: "Australia", he: "אוסטרליה" },
  generic: { en: "Other", he: "אחר" },
};

/** Distinct v1 maps: Israel and generic. English-speaking countries alias generic. */
export function curriculumBandLabels(
  curriculumId: PracticeCurriculumId,
): BandLabels {
  return curriculumId === "il" ? IL_BAND_LABELS : GENERIC_BAND_LABELS;
}

export function bandLabel(
  band: PracticeBand,
  curriculumId: PracticeCurriculumId,
  locale: PracticeLocale,
): string {
  const labels = curriculumBandLabels(curriculumId)[band];
  return locale === "he" ? labels.he : labels.en;
}

export function curriculumOptionLabel(
  curriculumId: PracticeCurriculumId,
  locale: PracticeLocale,
): string {
  const labels = CURRICULUM_OPTION_LABEL[curriculumId];
  return locale === "he" ? labels.he : labels.en;
}

export function labeledBands(
  curriculumId: PracticeCurriculumId,
  locale: PracticeLocale,
): { id: PracticeBand; label: string }[] {
  return (["elementary", "middle", "high"] as PracticeBand[])
    .filter(isPracticeBand)
    .map((id) => ({ id, label: bandLabel(id, curriculumId, locale) }));
}
